import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const START_ERROR_MESSAGES = {
  missing_authorization: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  invalid_session: '로그인 세션이 만료되었습니다. 다시 로그인해주세요.',
  invalid_profile_url: 'BlablaLink 사용자 프로필 URL을 확인해주세요.',
  missing_openid: '프로필 URL에 openid가 없습니다.',
  invalid_openid: '프로필 URL의 openid 형식이 올바르지 않습니다.',
  profile_read_failed: '사이트 계정 상태를 확인하지 못했습니다.',
  account_not_active: '현재 계정 상태에서는 게임 프로필을 인증할 수 없습니다.',
  game_profile_read_failed: '게임 프로필 연결 상태를 확인하지 못했습니다.',
  saved_game_profile_not_found: '저장된 BlablaLink 프로필을 찾지 못했습니다.',
  account_already_verified: '이미 게임 프로필이 연결된 계정입니다.',
  game_profile_already_claimed: '이미 다른 사이트 계정에 연결된 게임 프로필입니다.',
  game_profile_verification_in_use: '다른 계정에서 이 게임 프로필을 인증 중입니다.',
  personal_login_state_read_failed: '개인 로그인 계정 상태를 확인하지 못했습니다.',
  challenge_cleanup_failed: '이전 인증 요청을 정리하지 못했습니다.',
  challenge_rate_check_failed: '인증 요청 횟수를 확인하지 못했습니다.',
  challenge_read_failed: '진행 중인 인증 요청을 확인하지 못했습니다.',
  challenge_cancel_failed: '이전 인증 요청을 취소하지 못했습니다.',
  challenge_create_failed: '인증 코드를 발급하지 못했습니다.',
  rate_limited: '인증 코드를 너무 자주 발급했습니다. 잠시 후 다시 시도해주세요.',
  server_not_configured: '게임 프로필 인증 서버 설정을 확인해주세요.',
};

const CHECK_ERROR_MESSAGES = {
  ...START_ERROR_MESSAGES,
  invalid_challenge_id: '인증 요청 정보가 올바르지 않습니다. 새 코드를 발급해주세요.',
  challenge_not_found: '인증 요청을 찾지 못했습니다. 새 코드를 발급해주세요.',
  challenge_expired: '인증 코드가 만료되었습니다. 새 코드를 발급해주세요.',
  challenge_cancelled: '취소된 인증 요청입니다. 새 코드를 발급해주세요.',
  challenge_failed: '사용할 수 없는 인증 요청입니다. 새 코드를 발급해주세요.',
  attempt_limit_reached: '인증 확인 횟수를 초과했습니다. 새 코드를 발급해주세요.',
  challenge_update_failed: '인증 확인 상태를 저장하지 못했습니다.',
  invalid_stored_openid: '저장된 게임 프로필 식별값을 확인할 수 없습니다.',
  blablalink_timeout: 'BlablaLink 응답이 지연되고 있습니다.',
  blablalink_network_error: 'BlablaLink에 연결하지 못했습니다.',
  public_profile_unavailable: 'BlablaLink 공개 프로필을 불러오지 못했습니다.',
  public_profile_mismatch: '불러온 프로필과 인증 대상이 일치하지 않습니다.',
  blablalink_session_not_configured: 'BlablaLink 조회용 서버 세션이 설정되지 않았습니다.',
  blablalink_session_expired: 'BlablaLink 조회용 서버 세션이 만료되었습니다.',
  game_profile_details_unavailable: '게임 프로필 상세 정보를 불러오지 못했습니다.',
  nickname_unavailable: '게임 닉네임을 확인하지 못했습니다.',
  invalid_guild_id: '유니온 식별값 형식이 올바르지 않습니다.',
  invalid_guild_name: '유니온명 형식이 올바르지 않습니다.',
  game_profile_create_failed: '확인된 게임 프로필을 저장하지 못했습니다.',
  game_profile_update_failed: '게임 프로필의 최신 정보를 저장하지 못했습니다.',
  challenge_finalize_failed: '게임 프로필 인증을 완료하지 못했습니다.',
  account_relink_not_ready: '기존 계정 연결을 진행할 수 없는 인증 상태입니다.',
  claimed_game_profile_not_found: '기존 계정의 게임 프로필을 찾지 못했습니다.',
  personal_login_source_missing: '현재 개인 로그인 코드 연결 정보를 찾지 못했습니다.',
  target_auth_email_missing: '기존 계정의 내부 로그인 정보를 확인하지 못했습니다.',
  account_relink_failed: '기존 계정으로 로그인 정보를 연결하지 못했습니다.',
  session_link_failed: '기존 계정 로그인 세션 준비에 실패했습니다.',
  session_token_generation_failed: '기존 계정 로그인 토큰을 만들지 못했습니다.',
  session_generation_failed: '기존 계정 로그인 세션 발급에 실패했습니다.',
};

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }
  return supabase;
}

function createVerificationError(code, fallbackMessage, messages = START_ERROR_MESSAGES) {
  const error = new Error(
    messages[code] || fallbackMessage || '게임 프로필 인증 중 오류가 발생했습니다.',
  );
  error.code = code || 'unknown_error';
  return error;
}

async function readFunctionErrorCode(error) {
  try {
    const payload = await error?.context?.json();
    return {
      code: payload?.code || '',
      retryAfterSeconds: Number(payload?.retryAfterSeconds) || 0,
    };
  } catch {
    return { code: '', retryAfterSeconds: 0 };
  }
}

function normalizeVerifiedProfile(profile) {
  if (!profile) return null;
  return {
    openid: profile.openid || '',
    normalizedOpenid: profile.normalizedOpenid || profile.normalized_openid || '',
    nickname: profile.nickname || '',
    guildId: profile.guildId ?? profile.guild_id ?? null,
    guildName: profile.guildName ?? profile.guild_name ?? null,
    verifiedAt: profile.verifiedAt || profile.verified_at || '',
    lastCheckedAt: profile.lastCheckedAt || profile.last_checked_at || '',
  };
}

async function getAuthenticatedUser(client) {
  const { data: { user }, error } = await client.auth.getUser();
  if (error || !user?.id) throw createVerificationError('invalid_session');
  return user;
}

async function applyReturnedSession(client, session) {
  if (!session?.access_token || !session?.refresh_token) {
    throw createVerificationError('session_generation_failed', '', CHECK_ERROR_MESSAGES);
  }

  const { error } = await client.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  if (error) throw createVerificationError('session_generation_failed', error.message, CHECK_ERROR_MESSAGES);
}

async function relinkClaimedGameProfile(client, challengeId) {
  const { data, error } = await client.functions.invoke('relink-existing-game-profile', {
    body: { challengeId },
  });

  if (error) {
    const details = await readFunctionErrorCode(error);
    throw createVerificationError(details.code, error.message, CHECK_ERROR_MESSAGES);
  }
  if (!data?.ok || data.status !== 'verified' || !data.profile) {
    throw createVerificationError(data?.code || 'account_relink_failed', '', CHECK_ERROR_MESSAGES);
  }

  await applyReturnedSession(client, data.session);
  window.dispatchEvent(new CustomEvent('dororaid:game-profile-changed'));

  return {
    status: 'verified',
    profile: normalizeVerifiedProfile(data.profile),
    accountRelinked: true,
  };
}

async function recoverClaimedProfile(client, challengeId, originalCode, fallbackMessage) {
  try {
    return await relinkClaimedGameProfile(client, challengeId);
  } catch (relinkError) {
    if (originalCode === 'challenge_failed' && relinkError?.code === 'account_relink_not_ready') {
      throw createVerificationError(originalCode, fallbackMessage, CHECK_ERROR_MESSAGES);
    }
    throw relinkError;
  }
}

export async function loadCurrentGameProfile() {
  const client = requireSupabaseClient();
  const user = await getAuthenticatedUser(client);

  const { data, error } = await client
    .from('game_profiles')
    .select('openid,normalized_openid,nickname,guild_id,guild_name,verified_at,last_checked_at')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw createVerificationError('game_profile_read_failed', error.message);
  return normalizeVerifiedProfile(data);
}

export async function loadLatestVerifiedChallengeId() {
  const client = requireSupabaseClient();
  const user = await getAuthenticatedUser(client);

  const { data, error } = await client
    .from('game_profile_verification_challenges')
    .select('id')
    .eq('user_id', user.id)
    .eq('status', 'verified')
    .order('verified_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw createVerificationError('challenge_read_failed', error.message);
  return data?.id || '';
}

function readChallengeResponse(data) {
  if (!data?.ok || !data?.challenge?.id || !data?.challenge?.verificationCode || !data?.challenge?.expiresAt) {
    throw createVerificationError(data?.code);
  }
  return data.challenge;
}

export async function startGameProfileVerification(profileUrl) {
  const client = requireSupabaseClient();
  const normalizedUrl = String(profileUrl || '').trim();

  const { data, error } = await client.functions.invoke('start-game-profile-verification', {
    body: { profileUrl: normalizedUrl },
  });

  if (error) {
    const details = await readFunctionErrorCode(error);
    const verificationError = createVerificationError(details.code, error.message);
    verificationError.retryAfterSeconds = details.retryAfterSeconds;
    throw verificationError;
  }

  return readChallengeResponse(data);
}

export async function startCurrentGameProfileReverification() {
  const client = requireSupabaseClient();
  const { data, error } = await client.functions.invoke('reverify-game-profile', { body: {} });

  if (error) {
    const details = await readFunctionErrorCode(error);
    const verificationError = createVerificationError(details.code, error.message);
    verificationError.retryAfterSeconds = details.retryAfterSeconds;
    throw verificationError;
  }

  return readChallengeResponse(data);
}

export async function checkGameProfileVerification(challengeId) {
  const client = requireSupabaseClient();
  const normalizedChallengeId = String(challengeId || '').trim();

  const { data, error } = await client.functions.invoke('check-game-profile-verification', {
    body: { challengeId: normalizedChallengeId },
  });

  if (error) {
    const details = await readFunctionErrorCode(error);
    if (details.code === 'game_profile_already_claimed' || details.code === 'challenge_failed') {
      return recoverClaimedProfile(client, normalizedChallengeId, details.code, error.message);
    }
    throw createVerificationError(details.code, error.message, CHECK_ERROR_MESSAGES);
  }

  if (!data?.ok) {
    if (data?.code === 'game_profile_already_claimed' || data?.code === 'challenge_failed') {
      return recoverClaimedProfile(client, normalizedChallengeId, data.code, '');
    }
    throw createVerificationError(data?.code, '', CHECK_ERROR_MESSAGES);
  }

  if (data.status === 'verified') {
    return {
      status: 'verified',
      profile: normalizeVerifiedProfile(data.profile),
      accountRelinked: Boolean(data.accountRelinked),
    };
  }

  if (data.status === 'pending') {
    return {
      status: 'pending',
      code: data.code || 'verification_code_not_found',
      attemptCount: Number(data.attemptCount) || 0,
      expiresAt: data.expiresAt || '',
    };
  }

  throw createVerificationError(data?.code || 'unknown_status', '', CHECK_ERROR_MESSAGES);
}
